/**
 * @file Jump list + review progress for the whole-document Tables surface. One
 * clickable row per staged table (ordinal, source provenance, target-table
 * assignment, and a status band: skipped / needs-review / ready) that scrolls
 * and focuses that table's section, plus a "reviewed N / M tables" progress
 * counter. "Reviewed" is a derived proxy — a table that is assigned (not
 * skipped) with every source column mapped and a table-assignment confidence at
 * or above the warn threshold — since per-table review carries no persisted
 * state. Purely presentational: navigation is delegated to `onJump` and every
 * status is computed by `MappingReviewTables`.
 */
import { FC } from 'react';
import { Button, Progress, Tag, Typography, theme } from 'antd';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { mappingReviewLabels } from './mappingReviewLabels';
import type { MappingReviewTableStatus } from './MappingReviewTables';

const { Text, Title } = Typography;

const BAND: Record<MappingReviewTableStatus['band'], { color?: string; label: string }> = {
  excluded: { label: mappingReviewLabels.skip },
  attention: { color: 'warning', label: 'Needs review' },
  reviewed: { color: 'success', label: 'Ready' },
};

const MappingReviewTableToc: FC<{
  statuses: MappingReviewTableStatus[];
  reviewedCount: number;
  onJump: (groupId: string) => void;
}> = ({ statuses, reviewedCount, onJump }) => {
  const { token } = theme.useToken();
  const total = statuses.length;

  return (
    <FlexCol
      noGrow
      gap={5}
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: 10,
      }}
    >
      <FlexBox noGrow stretch gap={10} wrap alignStart>
        <Title level={5} style={{ margin: 0 }}>Tables</Title>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          reviewed {reviewedCount} / {total} tables
        </Text>
      </FlexBox>
      <Progress
        percent={total === 0 ? 0 : Math.round((reviewedCount / total) * 100)}
        size="small"
        showInfo={false}
        aria-label={`${reviewedCount} of ${total} tables reviewed`}
      />
      <FlexCol noGrow noGap>
        {statuses.map((status) => {
          const band = BAND[status.band];
          return (
            <Button
              key={status.groupId}
              type="text"
              block
              aria-label={`Jump to table ${status.ordinal}: ${status.provenance}`}
              onClick={() => onJump(status.groupId)}
              style={{ height: 'auto', padding: 5 }}
            >
              <FlexBox stretch gap={5} alignStart>
                <FlexBox noGrow justifyStart gap={5} style={{ minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    #{status.ordinal}
                  </Text>
                  <Text style={{ minWidth: 0 }}>
                    {status.provenance}
                  </Text>
                  <Text type="secondary" style={{ minWidth: 0 }}>
                    → {status.assignmentLabel}
                  </Text>
                </FlexBox>
                <Tag color={band.color} style={{ margin: 0 }}>
                  {band.label}
                </Tag>
              </FlexBox>
            </Button>
          );
        })}
      </FlexCol>
    </FlexCol>
  );
};

export default MappingReviewTableToc;
